import type { SupabaseClient } from '../supabase/client';
import {
  SignUpRecoveryError,
  type SignupInput,
  type SignupResult,
  type SignupErrorKey,
  type CustomerSigninErrorKey,
  type PasswordResetRequestInput,
  type PasswordResetConfirmInput,
  type PasswordResetRequestErrorKey,
  type PasswordResetConfirmErrorKey,
  type AdminAccessResult,
  type AdminEmailOtpRequestInput,
  type AuthOtpErrorKey,
  type CustomerPhoneOtpRequestInput,
  type OtpRequestResult,
  type OtpVerifyResult,
  type RequestPhoneOtpInput,
  type VerifyAdminEmailOtpInput,
  type VerifyCustomerPhoneOtpInput,
  type VerifyPhoneOtpInput,
  type PhoneOtpRequestResult,
  type PhoneOtpVerifyResult,
  type PhoneOtpErrorKey,
} from '../types/auth';

/** Options passed to signUpWithPassword. */
export interface SignUpOptions {
  /** Absolute URL the verification email link points to. Usually
   *  `${window.location.origin}/auth/callback` at the admin call site. */
  emailRedirectTo: string;
  /** Test-only: inject a deterministic sleep to avoid real timers. */
  sleep?: (ms: number) => Promise<void>;
  /** Arbitrary metadata threaded through to
   *  supabase.auth.signUp({ options: { data } }). Merchant signup (F-001)
   *  does NOT pass this and stays byte-identical to pre-F-008. */
  data?: Record<string, unknown>;
}

/** Bounded exponential-backoff schedule for the recovery signInWithPassword
 *  retry loop. Worst-case total wait: 2000 ms. */
export const SIGN_IN_RECOVERY_BACKOFF_MS = [500, 1500] as const;

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Create a Supabase Auth user and (if needed) establish a session.
 *
 * With Supabase "confirm email" ON, `signUp` returns `{ user, session: null }`.
 * We then call `signInWithPassword` with the same credentials to give the
 * unverified user a working session so they can proceed to /create-store.
 *
 * If the first recovery signin fails transiently (rate limit, network blip),
 * we retry up to two more times with exponential backoff (500 ms, 1500 ms)
 * before surfacing SignUpRecoveryError, which maps to a UI message telling
 * the user the account exists and to try again shortly.
 */
export async function signUpWithPassword(
  client: SupabaseClient,
  input: SignupInput,
  opts: SignUpOptions,
): Promise<SignupResult> {
  const sleep = opts.sleep ?? defaultSleep;

  const { data, error } = await client.auth.signUp({
    email: input.email,
    password: input.password,
    options: {
      emailRedirectTo: opts.emailRedirectTo,
      // Spread the data key conditionally so the F-001 merchant call
      // site (which never passes `data`) stays byte-identical to its
      // pre-F-008 payload — no spurious `data: undefined`.
      ...(opts.data ? { data: opts.data } : {}),
    },
  });
  if (error) throw error;
  if (!data.user) throw new Error('signUp returned no user');

  if (data.session) return { user: data.user, session: data.session };

  // Recovery loop: attempt 0 (no delay) + retries from SIGN_IN_RECOVERY_BACKOFF_MS.
  let lastError: unknown = null;
  const schedule = [0, ...SIGN_IN_RECOVERY_BACKOFF_MS];
  for (const delayMs of schedule) {
    if (delayMs > 0) await sleep(delayMs);
    const recovered = await client.auth.signInWithPassword(input);
    if (!recovered.error && recovered.data.session) {
      return { user: data.user, session: recovered.data.session };
    }
    lastError = recovered.error ?? new Error('signInWithPassword returned no session');
  }
  throw new SignUpRecoveryError(
    'Account created but sign-in recovery failed after retries',
    lastError,
  );
}

/** Plain wrapper used by callback flows and future signin form. */
export async function signInWithPassword(client: SupabaseClient, input: SignupInput) {
  const { data, error } = await client.auth.signInWithPassword(input);
  if (error) throw error;
  return data;
}

/** Narrow classification of merchant signup errors. */
type SignupErrorClass =
  | 'accountCreatedSignInFailed'
  | 'networkFailed'
  | 'rateLimited'
  | 'emailAlreadyExists'
  | 'unknown';

function classifySignupError(err: unknown): SignupErrorClass {
  if (!err || typeof err !== 'object') return 'unknown';

  // Precedence: SignUpRecoveryError first. Its .cause may itself be an
  // AuthError with a 429 status; we want the "account created, please
  // try again" message to win.
  if (err instanceof SignUpRecoveryError) return 'accountCreatedSignInFailed';

  if (err instanceof TypeError && /fetch/i.test(err.message ?? '')) {
    return 'networkFailed';
  }

  const maybe = err as {
    status?: number;
    code?: string;
    name?: string;
    message?: string;
  };

  if (maybe.name === 'AuthRetryableFetchError') return 'networkFailed';
  if (maybe.status === 429) return 'rateLimited';

  const alreadyExistsCodes = new Set(['user_already_exists', 'email_exists']);
  if (maybe.code && alreadyExistsCodes.has(maybe.code)) return 'emailAlreadyExists';
  if (
    (maybe.status === 400 || maybe.status === 409 || maybe.status === 422) &&
    typeof maybe.message === 'string' &&
    /already\s+registered|already\s+exists|user\s+already/i.test(maybe.message)
  ) {
    return 'emailAlreadyExists';
  }

  return 'unknown';
}

/**
 * Map an error from signUpWithPassword to a merchant (F-001) translation
 * key. Whitelist-based: unknown errors degrade to
 * auth.signup.errors.unknown.
 */
export function mapSignupError(err: unknown): SignupErrorKey {
  switch (classifySignupError(err)) {
    case 'accountCreatedSignInFailed':
      return 'auth.signup.errors.accountCreatedSignInFailed';
    case 'networkFailed':
      return 'auth.signup.errors.networkFailed';
    case 'rateLimited':
      return 'auth.signup.errors.rateLimited';
    case 'emailAlreadyExists':
      return 'auth.signup.errors.emailAlreadyExists';
    default:
      return 'auth.signup.errors.unknown';
  }
}

// ---------------------------------------------------------------------------
// F-009 — sign-out, password-reset services, classifiers, and mappers.
//
// Co-located with the signup service per clarifier D-9 — single file,
// shared classifier (classifySignupError) for the network/429/unknown
// branches so both auth lifecycles share one detection path.
// ---------------------------------------------------------------------------

/** Plain wrapper around `supabase.auth.signOut`. The cache-invalidation
 *  seam lives in `lib/signout.ts` (signOutAndInvalidate) so this service
 *  stays DI-free on React Query. */
export async function signOut(client: SupabaseClient): Promise<void> {
  const { error } = await client.auth.signOut();
  if (error) throw error;
}

export async function requestAdminEmailOtp(
  client: SupabaseClient,
  input: AdminEmailOtpRequestInput,
): Promise<OtpRequestResult> {
  const { data, error } = await client.auth.signInWithOtp({
    email: input.email,
    options: {
      shouldCreateUser: true,
      data: { merchant_signup: 'true', preferred_locale: input.preferredLocale ?? 'ar' },
      ...(input.captchaToken ? { captchaToken: input.captchaToken } : {}),
    },
  });
  if (error) throw error;
  return { messageId: data.messageId ?? null };
}

export async function verifyAdminEmailOtp(
  client: SupabaseClient,
  input: VerifyAdminEmailOtpInput,
): Promise<OtpVerifyResult> {
  const { data, error } = await client.auth.verifyOtp({
    email: input.email,
    token: input.token,
    type: 'email',
    ...(input.captchaToken ? { options: { captchaToken: input.captchaToken } } : {}),
  });
  if (error) throw error;
  return { user: data.user, session: data.session };
}

export async function getAdminAccess(
  client: SupabaseClient,
  userId: string,
): Promise<AdminAccessResult> {
  const { data: isAdminIdentity, error: identityError } = await client.rpc(
    'is_admin_auth_identity',
    { target_user_id: userId },
  );
  if (identityError) throw identityError;

  if (isAdminIdentity !== true) {
    return { state: 'unauthorized', storeId: null, userId };
  }

  const { data, error } = await client
    .from('store_members')
    .select('store_id')
    .eq('user_id', userId)
    .is('deleted_at', null)
    .limit(1)
    .maybeSingle();
  if (error) throw error;

  if (!data?.store_id) {
    return { state: 'pending', storeId: null, userId };
  }

  return { state: 'member', storeId: data.store_id, userId };
}

/** Options for `requestPasswordReset` — the absolute URL the Supabase
 *  recovery email link will point to. Usually
 *  `${window.location.origin}/account/reset-password?locale=<lang>`
 *  (plus an optional `&returnTo=` — see F-009 design §3b). */
export interface RequestPasswordResetOptions {
  redirectTo: string;
}

export async function requestPasswordReset(
  client: SupabaseClient,
  input: PasswordResetRequestInput,
  opts: RequestPasswordResetOptions,
): Promise<void> {
  const { error } = await client.auth.resetPasswordForEmail(input.email, {
    redirectTo: opts.redirectTo,
  });
  if (error) throw error;
}

export async function confirmPasswordReset(
  client: SupabaseClient,
  input: PasswordResetConfirmInput,
): Promise<void> {
  const { error } = await client.auth.updateUser({ password: input.password });
  if (error) throw error;
}

export async function requestPhoneOtp(
  client: SupabaseClient,
  input: RequestPhoneOtpInput,
): Promise<PhoneOtpRequestResult> {
  const { data, error } = await client.auth.signInWithOtp({
    phone: input.phone,
    options: {
      shouldCreateUser: input.shouldCreateUser,
      ...(input.data ? { data: input.data } : {}),
      ...(input.channel ? { channel: input.channel } : {}),
      ...(input.captchaToken ? { captchaToken: input.captchaToken } : {}),
    },
  });
  if (error) throw error;
  return { messageId: data.messageId ?? null };
}

export async function requestCustomerPhoneOtp(
  client: SupabaseClient,
  input: CustomerPhoneOtpRequestInput,
): Promise<PhoneOtpRequestResult> {
  const { data, error } = await client.auth.signInWithOtp({
    phone: input.phone,
    options: {
      shouldCreateUser: true,
      channel: input.channel ?? 'sms',
      ...(input.captchaToken ? { captchaToken: input.captchaToken } : {}),
    },
  });
  if (error) throw error;
  return { messageId: data.messageId ?? null };
}

/**
 * @deprecated F-AUTH-REBUILD removed admin phone OTP. Kept briefly so old
 * call sites fail closed while the admin app migrates to email OTP.
 */
export async function canRequestAdminPhoneOtp(
  _client: SupabaseClient,
  _phone: string,
): Promise<boolean> {
  return false;
}

export async function verifyPhoneOtp(
  client: SupabaseClient,
  input: VerifyPhoneOtpInput,
): Promise<PhoneOtpVerifyResult> {
  const { data, error } = await client.auth.verifyOtp({
    phone: input.phone,
    token: input.token,
    type: 'sms',
    ...(input.redirectTo || input.captchaToken
      ? {
          options: {
            ...(input.redirectTo ? { redirectTo: input.redirectTo } : {}),
            ...(input.captchaToken ? { captchaToken: input.captchaToken } : {}),
          },
        }
      : {}),
  });
  if (error) throw error;
  return { user: data.user, session: data.session };
}

export async function verifyCustomerPhoneOtp(
  client: SupabaseClient,
  input: VerifyCustomerPhoneOtpInput,
): Promise<OtpVerifyResult> {
  const { data, error } = await client.auth.verifyOtp({
    phone: input.phone,
    token: input.token,
    type: 'sms',
    ...(input.captchaToken ? { options: { captchaToken: input.captchaToken } } : {}),
  });
  if (error) throw error;
  return { user: data.user, session: data.session };
}

/** Narrow classification for signin errors. Reuses `classifySignupError`
 *  for the shared branches (network, rate limit, unknown) and adds the
 *  `invalidCredentials` branch unique to signin. */
type SigninErrorClass = 'invalidCredentials' | 'networkFailed' | 'rateLimited' | 'unknown';

/**
 * F-009 AC4 opacity guard: wrong-email AND wrong-password must BOTH map
 * to `'invalidCredentials'`. Supabase Auth v2 returns
 * `code: 'invalid_credentials'`; older SDKs used `'invalid_login_credentials'`;
 * the same response's OAuth2 top-level slot carries `error: 'invalid_grant'`.
 * We accept all three, plus a regex fallback on the message, plus the
 * `AuthApiError` instance with only a 400 status + message. Unit tests
 * in `auth.service.test.ts` feed all 5 real-world shapes through
 * `mapCustomerSigninError` and assert every one returns the
 * `invalidCredentials` key (none falls through to `unknown`).
 */
function classifySigninError(err: unknown): SigninErrorClass {
  const base = classifySignupError(err);
  if (base === 'networkFailed') return 'networkFailed';
  if (base === 'rateLimited') return 'rateLimited';

  if (err && typeof err === 'object') {
    const maybe = err as {
      code?: string;
      error?: string;
      error_description?: string;
      message?: string;
    };
    const INVALID_CREDENTIAL_CODES = new Set([
      'invalid_credentials', // Supabase Auth v2 current
      'invalid_login_credentials', // Supabase Auth v2 legacy
      'invalid_grant', // OAuth2 top-level error slot
    ]);
    if (maybe.code && INVALID_CREDENTIAL_CODES.has(maybe.code)) {
      return 'invalidCredentials';
    }
    if (maybe.error && INVALID_CREDENTIAL_CODES.has(maybe.error)) {
      return 'invalidCredentials';
    }
    const haystack = `${maybe.message ?? ''} ${maybe.error_description ?? ''}`;
    if (/invalid\s+login\s+credentials|invalid\s+credentials/i.test(haystack)) {
      return 'invalidCredentials';
    }
  }
  return 'unknown';
}

export function mapCustomerSigninError(err: unknown): CustomerSigninErrorKey {
  switch (classifySigninError(err)) {
    case 'invalidCredentials':
      return 'customer.login.errors.invalidCredentials';
    case 'rateLimited':
      return 'customer.login.errors.rateLimited';
    case 'networkFailed':
      return 'customer.login.errors.networkFailed';
    default:
      return 'customer.login.errors.unknown';
  }
}

/** Password-reset *request* errors — reuses the shared classifier for
 *  network/429/unknown. There are no credentials involved, so the
 *  invalid-credentials branch does not apply. */
export function mapPasswordResetRequestError(err: unknown): PasswordResetRequestErrorKey {
  const base = classifySignupError(err);
  if (base === 'rateLimited') return 'customer.passwordReset.request.errors.rateLimited';
  if (base === 'networkFailed') return 'customer.passwordReset.request.errors.networkFailed';
  return 'customer.passwordReset.request.errors.unknown';
}

/** Password-reset *confirm* errors — additionally surfaces
 *  "password too short / weak" from Supabase's server-side validation. */
export function mapPasswordResetConfirmError(err: unknown): PasswordResetConfirmErrorKey {
  if (err && typeof err === 'object') {
    const maybe = err as { code?: string; message?: string };
    if (
      maybe.code === 'weak_password' ||
      maybe.code === 'password_too_short' ||
      /password.*short|password.*weak|at least.*characters/i.test(maybe.message ?? '')
    ) {
      return 'customer.passwordReset.confirm.errors.passwordTooShort';
    }
  }
  const base = classifySignupError(err);
  if (base === 'rateLimited') return 'customer.passwordReset.confirm.errors.rateLimited';
  if (base === 'networkFailed') return 'customer.passwordReset.confirm.errors.networkFailed';
  return 'customer.passwordReset.confirm.errors.unknown';
}

export function mapAdminEmailOtpError(err: unknown): AuthOtpErrorKey {
  const base = classifySignupError(err);
  if (base === 'rateLimited') return 'admin.signIn.errors.rateLimited';
  if (base === 'networkFailed') return 'admin.signIn.errors.networkFailed';

  if (err && typeof err === 'object') {
    const maybe = err as { code?: string; error?: string; message?: string };
    const haystack = `${maybe.code ?? ''} ${maybe.error ?? ''} ${maybe.message ?? ''}`;
    if (/otp|token|code|invalid|expired/i.test(haystack)) {
      return 'admin.signIn.errors.invalidCode';
    }
  }

  return 'admin.signIn.errors.unknown';
}

export function mapCustomerPhoneOtpError(err: unknown): AuthOtpErrorKey {
  const base = classifySignupError(err);
  if (base === 'rateLimited') return 'auth.phoneOtp.errors.rateLimited';
  if (base === 'networkFailed') return 'auth.phoneOtp.errors.networkFailed';

  if (err && typeof err === 'object') {
    const maybe = err as { code?: string; error?: string; message?: string };
    if (maybe.code === 'profile_ensure_failed') {
      return 'auth.phoneOtp.errors.profileEnsureFailed';
    }
    const haystack = `${maybe.code ?? ''} ${maybe.error ?? ''} ${maybe.message ?? ''}`;
    if (/otp|token|code|invalid|expired/i.test(haystack)) {
      return 'auth.phoneOtp.errors.invalidCode';
    }
  }

  return 'auth.phoneOtp.errors.unknown';
}

export function mapPhoneOtpError(err: unknown): PhoneOtpErrorKey {
  const base = classifySignupError(err);
  if (base === 'rateLimited') return 'auth.phoneOtp.errors.rateLimited';
  if (base === 'networkFailed') return 'auth.phoneOtp.errors.networkFailed';

  if (err && typeof err === 'object') {
    const maybe = err as { code?: string; error?: string; message?: string };
    if (maybe.code === 'customer_account_missing') {
      return 'auth.phoneOtp.errors.customerAccountMissing';
    }
    if (maybe.code === 'admin_account_missing') {
      return 'auth.phoneOtp.errors.adminAccountMissing';
    }
    if (maybe.code === 'rate_limited') {
      return 'auth.phoneOtp.errors.rateLimited';
    }
    const haystack = `${maybe.code ?? ''} ${maybe.error ?? ''} ${maybe.message ?? ''}`;
    if (/otp|token|code|invalid|expired/i.test(haystack)) {
      return 'auth.phoneOtp.errors.invalidCode';
    }
  }

  return 'auth.phoneOtp.errors.unknown';
}
